import { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { LayoutTemplate, Sparkles, Zap, Hand } from 'lucide-react';
import { GlassCard, GlassSurface } from '@/components/glass';
import { PageHeader } from '@/components/ui';
import { FormModal, DetailModal, ConfirmModal } from '@/components/modal';
import {
  ColorControl,
  SliderControl,
  TextControl,
  ToggleControl,
  SelectControl,
  SectionHeader,
  PreviewBox,
  ControlsPanel,
  ResetButton,
} from './design-system/DesignControls';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';

// ---------------------------------------------------------------------------
// Section 1 -- Glass Variants
// ---------------------------------------------------------------------------

const GLASS_VARIANT_DEFAULTS: Record<
  string,
  { bg: string; opacity: number; blur: number; borderColor: string; borderWidth: number }
> = {
  card: { bg: '#ffffff', opacity: 0.45, blur: 12, borderColor: '#94a3b8', borderWidth: 1 },
  nav: { bg: '#ffffff', opacity: 0.55, blur: 16, borderColor: '#94a3b8', borderWidth: 1 },
  modal: { bg: '#ffffff', opacity: 0.7, blur: 20, borderColor: '#94a3b8', borderWidth: 1 },
  tinted: { bg: '#1565c0', opacity: 0.08, blur: 12, borderColor: '#94a3b8', borderWidth: 1 },
};

function GlassVariantsSection() {
  const { t } = useTranslation();
  const [variants, setVariants] = useState(GLASS_VARIANT_DEFAULTS);

  const update = (variant: string, key: string, value: number | string) =>
    setVariants((prev) => ({ ...prev, [variant]: { ...prev[variant], [key]: value } }));

  return (
    <motion.div variants={staggerItem}>
      <GlassCard title={t('designSystem.glassVariants')} icon={<LayoutTemplate size={20} />}>
        <div className="space-y-8">
          {(Object.keys(GLASS_VARIANT_DEFAULTS) as string[]).map((v) => {
            const s = variants[v];
            return (
              <div key={v} className="grid md:grid-cols-2 gap-6">
                <ControlsPanel>
                  <SectionHeader>{v}</SectionHeader>
                  <ResetButton
                    onClick={() =>
                      setVariants((prev) => ({ ...prev, [v]: GLASS_VARIANT_DEFAULTS[v] }))
                    }
                  />
                  <ColorControl label="Background" value={s.bg} onChange={(c) => update(v, 'bg', c)} />
                  <SliderControl
                    label="Opacity"
                    value={s.opacity}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(n) => update(v, 'opacity', n)}
                  />
                  <SliderControl
                    label="Backdrop Blur"
                    value={s.blur}
                    min={0}
                    max={40}
                    step={1}
                    unit="px"
                    onChange={(n) => update(v, 'blur', n)}
                  />
                  <ColorControl
                    label="Border Color"
                    value={s.borderColor}
                    onChange={(c) => update(v, 'borderColor', c)}
                  />
                  <SliderControl
                    label="Border Width"
                    value={s.borderWidth}
                    min={0}
                    max={3}
                    step={0.5}
                    unit="px"
                    onChange={(n) => update(v, 'borderWidth', n)}
                  />
                </ControlsPanel>

                <PreviewBox>
                  <GlassSurface
                    variant={v as 'card' | 'nav' | 'modal' | 'tinted'}
                    style={{
                      background: `color-mix(in srgb, ${s.bg} ${Math.round(s.opacity * 100)}%, transparent)`,
                      backdropFilter: `blur(${s.blur}px)`,
                      WebkitBackdropFilter: `blur(${s.blur}px)`,
                      border: `${s.borderWidth}px solid ${s.borderColor}`,
                      padding: '1.5rem',
                      borderRadius: '1rem',
                    }}
                  >
                    <p className="text-sm font-medium" style={{ color: ADMIN_COLORS.navy }}>
                      GlassSurface variant=&quot;{v}&quot;
                    </p>
                    <p className="text-xs mt-1" style={{ color: ADMIN_COLORS.textSecondary }}>
                      Adjust controls to see live changes.
                    </p>
                  </GlassSurface>
                </PreviewBox>
              </div>
            );
          })}
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 -- GlassCard Deep Dive
// ---------------------------------------------------------------------------

interface GlassCardControls {
  cardBg: string;
  borderColor: string;
  borderWidth: number;
  borderRadius: number;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;
  shadowColor: string;
  showHeader: boolean;
  headerBg: string;
  headerTextColor: string;
  headerFontSize: number;
  headerFontWeight: string;
  headerPaddingY: number;
  showHeaderBorder: boolean;
  headerBorderColor: string;
  showFooter: boolean;
  footerBg: string;
  paddingPreset: string;
  customPadding: number;
  titleText: string;
  showIcon: boolean;
  showHeaderAction: boolean;
}

const GLASS_CARD_DEFAULTS: GlassCardControls = {
  cardBg: '#ffffff',
  borderColor: '#94a3b8',
  borderWidth: 1,
  borderRadius: 16,
  shadowX: 0,
  shadowY: 4,
  shadowBlur: 16,
  shadowColor: '#1565c0',
  showHeader: true,
  headerBg: '#f8fafc',
  headerTextColor: '#1e293b',
  headerFontSize: 14,
  headerFontWeight: '600',
  headerPaddingY: 12,
  showHeaderBorder: true,
  headerBorderColor: '#e2e8f0',
  showFooter: false,
  footerBg: '#f8fafc',
  paddingPreset: 'default',
  customPadding: 16,
  titleText: 'Card Title',
  showIcon: true,
  showHeaderAction: true,
};

function GlassCardSection() {
  const [c, setC] = useState<GlassCardControls>(GLASS_CARD_DEFAULTS);

  const set = <K extends keyof GlassCardControls>(key: K, value: GlassCardControls[K]) =>
    setC((prev) => ({ ...prev, [key]: value }));

  const paddingMap: Record<string, number> = { default: 16, compact: 8, none: 0 };
  const resolvedPadding =
    c.paddingPreset === 'custom' ? c.customPadding : (paddingMap[c.paddingPreset] ?? 16);

  return (
    <motion.div variants={staggerItem}>
      <GlassCard title="GlassCard Deep Dive" icon={<LayoutTemplate size={20} />}>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Controls */}
          <ControlsPanel>
            <ResetButton onClick={() => setC(GLASS_CARD_DEFAULTS)} />

            <SectionHeader>Card Surface</SectionHeader>
            <ColorControl label="Card Background" value={c.cardBg} onChange={(v) => set('cardBg', v)} />
            <ColorControl label="Border Color" value={c.borderColor} onChange={(v) => set('borderColor', v)} />
            <SliderControl label="Border Width" value={c.borderWidth} min={0} max={4} step={0.5} unit="px" onChange={(v) => set('borderWidth', v)} />
            <SliderControl label="Border Radius" value={c.borderRadius} min={0} max={32} step={1} unit="px" onChange={(v) => set('borderRadius', v)} />

            <SectionHeader>Shadow</SectionHeader>
            <SliderControl label="Shadow X" value={c.shadowX} min={-20} max={20} step={1} unit="px" onChange={(v) => set('shadowX', v)} />
            <SliderControl label="Shadow Y" value={c.shadowY} min={-20} max={20} step={1} unit="px" onChange={(v) => set('shadowY', v)} />
            <SliderControl label="Shadow Blur" value={c.shadowBlur} min={0} max={40} step={1} unit="px" onChange={(v) => set('shadowBlur', v)} />
            <ColorControl label="Shadow Color" value={c.shadowColor} onChange={(v) => set('shadowColor', v)} />

            <SectionHeader>Header</SectionHeader>
            <ToggleControl label="Show Header" value={c.showHeader} onChange={(v) => set('showHeader', v)} />
            {c.showHeader && (
              <>
                <ColorControl label="Header BG" value={c.headerBg} onChange={(v) => set('headerBg', v)} />
                <ColorControl label="Header Text" value={c.headerTextColor} onChange={(v) => set('headerTextColor', v)} />
                <SliderControl label="Font Size" value={c.headerFontSize} min={10} max={18} step={1} unit="px" onChange={(v) => set('headerFontSize', v)} />
                <SelectControl
                  label="Font Weight"
                  value={c.headerFontWeight}
                  options={[
                    { label: '400 (Normal)', value: '400' },
                    { label: '500 (Medium)', value: '500' },
                    { label: '600 (Semibold)', value: '600' },
                    { label: '700 (Bold)', value: '700' },
                  ]}
                  onChange={(v) => set('headerFontWeight', v)}
                />
                <SliderControl label="Header Padding Y" value={c.headerPaddingY} min={4} max={20} step={1} unit="px" onChange={(v) => set('headerPaddingY', v)} />
                <ToggleControl label="Header-Body Border" value={c.showHeaderBorder} onChange={(v) => set('showHeaderBorder', v)} />
                {c.showHeaderBorder && (
                  <ColorControl label="Header Border Color" value={c.headerBorderColor} onChange={(v) => set('headerBorderColor', v)} />
                )}
              </>
            )}

            <SectionHeader>Footer</SectionHeader>
            <ToggleControl label="Show Footer" value={c.showFooter} onChange={(v) => set('showFooter', v)} />
            {c.showFooter && (
              <ColorControl label="Footer BG" value={c.footerBg} onChange={(v) => set('footerBg', v)} />
            )}

            <SectionHeader>Content</SectionHeader>
            <SelectControl
              label="Padding"
              value={c.paddingPreset}
              options={[
                { label: 'Default (16px)', value: 'default' },
                { label: 'Compact (8px)', value: 'compact' },
                { label: 'None (0px)', value: 'none' },
                { label: 'Custom', value: 'custom' },
              ]}
              onChange={(v) => set('paddingPreset', v)}
            />
            {c.paddingPreset === 'custom' && (
              <SliderControl label="Custom Padding" value={c.customPadding} min={0} max={40} step={1} unit="px" onChange={(v) => set('customPadding', v)} />
            )}
            <TextControl label="Title Text" value={c.titleText} onChange={(v) => set('titleText', v)} />
            <ToggleControl label="Show Icon" value={c.showIcon} onChange={(v) => set('showIcon', v)} />
            <ToggleControl label="Show Header Action" value={c.showHeaderAction} onChange={(v) => set('showHeaderAction', v)} />
          </ControlsPanel>

          {/* Preview */}
          <PreviewBox>
            <div
              style={{
                background: `color-mix(in srgb, ${c.cardBg} 45%, transparent)`,
                border: `${c.borderWidth}px solid ${c.borderColor}`,
                borderRadius: `${c.borderRadius}px`,
                boxShadow: `${c.shadowX}px ${c.shadowY}px ${c.shadowBlur}px color-mix(in srgb, ${c.shadowColor} 12%, transparent)`,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              {c.showHeader && (
                <div
                  style={{
                    background: c.headerBg,
                    padding: `${c.headerPaddingY}px 16px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: c.showHeaderBorder
                      ? `1px solid ${c.headerBorderColor}`
                      : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {c.showIcon && (
                      <span style={{ color: c.headerTextColor, opacity: 0.7 }}>
                        <LayoutTemplate size={16} />
                      </span>
                    )}
                    <span
                      style={{
                        color: c.headerTextColor,
                        fontSize: `${c.headerFontSize}px`,
                        fontWeight: Number(c.headerFontWeight),
                      }}
                    >
                      {c.titleText}
                    </span>
                  </div>
                  {c.showHeaderAction && (
                    <span
                      className="px-2 py-0.5 rounded-md text-xs font-medium"
                      style={{
                        background: ADMIN_COLORS.primarySoft,
                        color: ADMIN_COLORS.primaryText,
                      }}
                    >
                      Action
                    </span>
                  )}
                </div>
              )}

              {/* Body */}
              <div style={{ padding: `${resolvedPadding}px` }}>
                <p className="text-sm leading-relaxed" style={{ color: ADMIN_COLORS.textSecondary }}>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor
                  incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
                  exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                </p>
              </div>

              {/* Footer */}
              {c.showFooter && (
                <div
                  style={{
                    background: c.footerBg,
                    padding: '10px 16px',
                    borderTop: `1px solid ${c.borderColor}`,
                  }}
                >
                  <span className="text-xs" style={{ color: ADMIN_COLORS.textTertiary }}>
                    Footer content
                  </span>
                </div>
              )}
            </div>
          </PreviewBox>
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 -- Modal System
// ---------------------------------------------------------------------------

interface ModalControls {
  surfaceColor: string;
  headerColor: string;
  backdropBlur: number;
  borderColor: string;
  confirmBtnColor: string;
  cancelBtnColor: string;
  borderRadius: number;
  confirmVariant: 'danger' | 'warning' | 'info';
}

const MODAL_DEFAULTS: ModalControls = {
  surfaceColor: '#ffffff',
  headerColor: '#f8fafc',
  backdropBlur: 8,
  borderColor: '#e2e8f0',
  confirmBtnColor: '#1565c0',
  cancelBtnColor: '#64748b',
  borderRadius: 16,
  confirmVariant: 'danger',
};

function ModalSystemSection() {
  const { t } = useTranslation();
  const [c, setC] = useState<ModalControls>(MODAL_DEFAULTS);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const set = <K extends keyof ModalControls>(key: K, value: ModalControls[K]) =>
    setC((prev) => ({ ...prev, [key]: value }));

  const modalStyle: React.CSSProperties = {
    '--modal-surface': c.surfaceColor,
    '--modal-header': c.headerColor,
    '--modal-border': c.borderColor,
    '--modal-radius': `${c.borderRadius}px`,
    '--modal-backdrop-blur': `${c.backdropBlur}px`,
    '--modal-confirm': c.confirmBtnColor,
    '--modal-cancel': c.cancelBtnColor,
  } as React.CSSProperties;

  const btnClass =
    'px-4 py-2 rounded-xl font-semibold glass-surface transition-all active:scale-[0.98] cursor-pointer text-sm';

  return (
    <motion.div variants={staggerItem}>
      <GlassCard title={t('designSystem.modalSystem')} icon={<Sparkles size={20} />}>
        <div className="grid md:grid-cols-2 gap-6">
          <ControlsPanel>
            <ResetButton onClick={() => setC(MODAL_DEFAULTS)} />
            <ColorControl label="Surface Color" value={c.surfaceColor} onChange={(v) => set('surfaceColor', v)} />
            <ColorControl label="Header Color" value={c.headerColor} onChange={(v) => set('headerColor', v)} />
            <SliderControl label="Backdrop Blur" value={c.backdropBlur} min={0} max={20} step={1} unit="px" onChange={(v) => set('backdropBlur', v)} />
            <ColorControl label="Border Color" value={c.borderColor} onChange={(v) => set('borderColor', v)} />
            <ColorControl label="Confirm Button" value={c.confirmBtnColor} onChange={(v) => set('confirmBtnColor', v)} />
            <ColorControl label="Cancel Button" value={c.cancelBtnColor} onChange={(v) => set('cancelBtnColor', v)} />
            <SliderControl label="Border Radius" value={c.borderRadius} min={0} max={32} step={1} unit="px" onChange={(v) => set('borderRadius', v)} />

            <SectionHeader>ConfirmModal Variant</SectionHeader>
            <div className="flex gap-2">
              {(['danger', 'warning', 'info'] as const).map((variant) => (
                <button
                  key={variant}
                  type="button"
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    c.confirmVariant === variant ? 'ring-2 ring-offset-1' : 'opacity-60'
                  }`}
                  style={{
                    background:
                      variant === 'danger'
                        ? ADMIN_COLORS.dangerBg
                        : variant === 'warning'
                          ? ADMIN_COLORS.warningBg
                          : ADMIN_COLORS.infoBg,
                    color:
                      variant === 'danger'
                        ? ADMIN_COLORS.dangerText
                        : variant === 'warning'
                          ? ADMIN_COLORS.warningText
                          : ADMIN_COLORS.infoText,
                  }}
                  onClick={() => set('confirmVariant', variant)}
                >
                  {variant}
                </button>
              ))}
            </div>
          </ControlsPanel>

          <PreviewBox>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                className={btnClass}
                style={{ border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: ADMIN_COLORS.shadow }}
                onClick={() => setFormOpen(true)}
              >
                {t('designSystem.openFormModal')}
              </button>
              <button
                type="button"
                className={btnClass}
                style={{ border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: ADMIN_COLORS.shadow }}
                onClick={() => setDetailOpen(true)}
              >
                {t('designSystem.openDetailModal')}
              </button>
              <button
                type="button"
                className={btnClass}
                style={{ border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: ADMIN_COLORS.shadow }}
                onClick={() => setConfirmOpen(true)}
              >
                {t('designSystem.openConfirmModal')}
              </button>
            </div>

            <div style={modalStyle}>
              <FormModal
                isOpen={formOpen}
                onClose={() => setFormOpen(false)}
                title="Sample Form"
                onSubmit={() => setFormOpen(false)}
                submitLabel="Save"
              >
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Name"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: ADMIN_COLORS.ice,
                      border: `1px solid ${c.borderColor}`,
                    }}
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: ADMIN_COLORS.ice,
                      border: `1px solid ${c.borderColor}`,
                    }}
                  />
                </div>
              </FormModal>

              <DetailModal
                isOpen={detailOpen}
                onClose={() => setDetailOpen(false)}
                ariaLabel="Detail preview"
              >
                <div className="p-6 space-y-3">
                  <h3 className="text-lg font-semibold" style={{ color: ADMIN_COLORS.navy }}>
                    Detail View
                  </h3>
                  <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>
                    This is a detail modal with custom styling overrides applied through CSS
                    variables.
                  </p>
                </div>
              </DetailModal>

              <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => setConfirmOpen(false)}
                title="Confirm Action"
                message="Are you sure you want to proceed with this action?"
                variant={c.confirmVariant}
              />
            </div>
          </PreviewBox>
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Section 4 -- Animations
// ---------------------------------------------------------------------------

function useCustomAnimatedNumber(target: number, duration: number): number {
  const [display, setDisplay] = useState(target);
  const prev = useRef(target);
  const rafId = useRef<number>(0);

  useEffect(() => {
    const from = prev.current;
    const to = target;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // cubic ease-in-out
      const eased =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      setDisplay(from + (to - from) * eased);
      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      } else {
        prev.current = to;
      }
    };

    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId.current);
  }, [target, duration]);

  return display;
}

function AnimationsSection() {
  const { t } = useTranslation();

  // Stagger controls
  const [staggerDelay, setStaggerDelay] = useState(0.06);
  const [childrenDelay, setChildrenDelay] = useState(0.1);
  const [springStiffness, setSpringStiffness] = useState(350);
  const [springDamping, setSpringDamping] = useState(30);
  const [animKey, setAnimKey] = useState(0);

  // Animated number controls
  const [animDuration, setAnimDuration] = useState(800);
  const [targetValue, setTargetValue] = useState(500);
  const animatedNum = useCustomAnimatedNumber(targetValue, animDuration);

  const customContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: childrenDelay,
      },
    },
  };

  const customItem = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: springStiffness,
        damping: springDamping,
      },
    },
  };

  const btnClass =
    'px-4 py-2 rounded-xl font-semibold glass-surface transition-all active:scale-[0.98] cursor-pointer text-sm';

  return (
    <motion.div variants={staggerItem}>
      <GlassCard title={t('designSystem.animations')} icon={<Sparkles size={20} />}>
        <div className="grid md:grid-cols-2 gap-6">
          <ControlsPanel>
            <SectionHeader>Stagger Container</SectionHeader>
            <SliderControl
              label="Stagger Delay"
              value={staggerDelay}
              min={0}
              max={0.2}
              step={0.01}
              unit="s"
              onChange={setStaggerDelay}
            />
            <SliderControl
              label="Children Delay"
              value={childrenDelay}
              min={0}
              max={0.5}
              step={0.01}
              unit="s"
              onChange={setChildrenDelay}
            />
            <SliderControl
              label="Spring Stiffness"
              value={springStiffness}
              min={100}
              max={500}
              step={10}
              onChange={setSpringStiffness}
            />
            <SliderControl
              label="Spring Damping"
              value={springDamping}
              min={10}
              max={50}
              step={1}
              onChange={setSpringDamping}
            />
            <button
              type="button"
              className={btnClass}
              style={{ border: `1px solid ${ADMIN_COLORS.border}`, boxShadow: ADMIN_COLORS.shadow }}
              onClick={() => setAnimKey((k) => k + 1)}
            >
              {t('designSystem.replayAnimation')}
            </button>

            <SectionHeader>useAnimatedNumber</SectionHeader>
            <SliderControl
              label="Duration"
              value={animDuration}
              min={200}
              max={2000}
              step={100}
              unit="ms"
              onChange={setAnimDuration}
            />
            <SliderControl
              label="Target Value"
              value={targetValue}
              min={0}
              max={1000}
              step={1}
              onChange={setTargetValue}
            />
          </ControlsPanel>

          <PreviewBox>
            <div className="space-y-6">
              {/* Stagger preview */}
              <div>
                <p className="text-xs font-medium mb-3" style={{ color: ADMIN_COLORS.textTertiary }}>
                  Stagger Animation
                </p>
                <motion.div
                  key={animKey}
                  variants={customContainer}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-3 gap-2"
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <motion.div
                      key={i}
                      variants={customItem}
                      className="rounded-xl p-3 text-center"
                      style={{
                        background: ADMIN_COLORS.primarySoft,
                        border: `1px solid ${ADMIN_COLORS.border}`,
                      }}
                    >
                      <span
                        className="text-xs font-semibold"
                        style={{ color: ADMIN_COLORS.primaryText }}
                      >
                        {i + 1}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              {/* Animated number preview */}
              <div>
                <p className="text-xs font-medium mb-3" style={{ color: ADMIN_COLORS.textTertiary }}>
                  Animated Number
                </p>
                <div
                  className="rounded-xl p-4 text-center"
                  style={{
                    background: ADMIN_COLORS.surface,
                    border: `1px solid ${ADMIN_COLORS.border}`,
                  }}
                >
                  <span
                    className="text-3xl font-bold tabular-nums"
                    style={{ color: ADMIN_COLORS.primary }}
                  >
                    {Math.round(animatedNum).toLocaleString()}
                  </span>
                  <p className="text-xs mt-1" style={{ color: ADMIN_COLORS.textTertiary }}>
                    Duration: {animDuration}ms
                  </p>
                </div>
              </div>
            </div>
          </PreviewBox>
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Section 5 -- Haptics
// ---------------------------------------------------------------------------

function HapticsSection() {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState('15');

  const triggerCustom = useCallback(() => {
    const parts = pattern
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);
    if (parts.length === 0) return;
    triggerHapticFeedback(parts.length === 1 ? parts[0] : parts);
  }, [pattern]);

  const presets: { label: string; value: number | number[] }[] = [
    { label: 'Light (15ms)', value: 15 },
    { label: 'Medium (30ms)', value: 30 },
    { label: 'Heavy (50,30,50)', value: [50, 30, 50] },
  ];

  const btnClass =
    'px-4 py-2 rounded-xl font-semibold glass-surface transition-all active:scale-[0.98] cursor-pointer text-sm';

  return (
    <motion.div variants={staggerItem}>
      <GlassCard title={t('designSystem.haptics')} icon={<Hand size={20} />}>
        <div className="grid md:grid-cols-2 gap-6">
          <ControlsPanel>
            <TextControl
              label="Pattern (comma-separated ms)"
              value={pattern}
              onChange={setPattern}
            />
            <button
              type="button"
              className={btnClass}
              style={{
                border: `1px solid ${ADMIN_COLORS.border}`,
                boxShadow: ADMIN_COLORS.shadow,
                background: ADMIN_COLORS.primarySoft,
                color: ADMIN_COLORS.primaryText,
              }}
              onClick={triggerCustom}
            >
              <span className="flex items-center gap-2 justify-center">
                <Zap size={14} />
                Trigger Custom
              </span>
            </button>
          </ControlsPanel>

          <PreviewBox>
            <div className="flex flex-col gap-3">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className={btnClass}
                  style={{
                    border: `1px solid ${ADMIN_COLORS.border}`,
                    boxShadow: ADMIN_COLORS.shadow,
                  }}
                  onClick={() => triggerHapticFeedback(p.value)}
                >
                  <span className="flex items-center gap-2 justify-center">
                    <Zap size={14} />
                    {p.label}
                  </span>
                </button>
              ))}
            </div>
          </PreviewBox>
        </div>
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DesignPatternsPage() {
  const { t } = useTranslation();

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={staggerItem}>
        <PageHeader
          title={t('designSystem.patternsTitle')}
          subtitle={t('designSystem.patternsSubtitle')}
          icon={<LayoutTemplate size={24} />}
        />
      </motion.div>

      <GlassVariantsSection />
      <GlassCardSection />
      <ModalSystemSection />
      <AnimationsSection />
      <HapticsSection />
    </motion.div>
  );
}
